import { Button } from 'baseui/button';
import { StyledBody } from 'baseui/card';
import {
  Modal,
  ModalBody,
  ModalButton,
  ModalFooter,
  ModalHeader,
} from 'baseui/modal';
import { Select } from 'baseui/select';
import { Spinner } from 'baseui/spinner';
import { Tag } from 'baseui/tag';
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CreditCard, CheckCircle } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { Card } from 'components/Card';
import { Input } from 'components/Input';
import {
  useCreateCheckoutSessionMutation,
  useConfirmPaymentMutation,
} from 'services/api/payment/endpoints';
import {
  HelcimTransactionResponse,
  HelcimPayResponseSchema,
  HelcimPayResponse,
} from 'services/api/payment/types';
import { useLocale } from 'utils/hooks/useLocale';

const isHelcimPayResponse = (data: unknown): data is HelcimPayResponse => {
  try {
    HelcimPayResponseSchema.parse(data);
    return true;
  } catch {
    return false;
  }
};

type PaymentState = 'idle' | 'processing' | 'confirming' | 'success' | 'error';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  amount: number;
  remainingBalance: number;
  invoiceId: number;
  currency: 'USD' | 'CAD';
  onPaymentComplete: (response: HelcimTransactionResponse) => void;
}

export const PaymentModal = ({
  isOpen,
  onClose,
  amount,
  remainingBalance,
  invoiceId,
  currency,
  onPaymentComplete,
}: PaymentModalProps) => {
  const navigate = useNavigate();
  const { t } = useLocale();

  const [paymentAmount, setPaymentAmount] = useState<number | string>(
    remainingBalance,
  );
  const [paymentState, setPaymentState] = useState<PaymentState>('idle');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [createCheckoutSession, { isLoading: isCreatingCheckout }] =
    useCreateCheckoutSessionMutation();
  const [confirmPayment, { isLoading: isConfirming }] =
    useConfirmPaymentMutation();

  const isProcessing = isCreatingCheckout || isConfirming;

  const formatAmount = (value: number | string): string => {
    if (typeof value === 'number') {
      return value.toFixed(2);
    }
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      return value;
    }
    return parseFloat(value || '0').toFixed(2);
  };

  // TODO: Update proper handling of `payment/checkout` API
  const createCheckoutWithRetry = async (
    createCheckoutSession: any,
    params: { amount: number; currency: string },
  ): Promise<any> => {
    const attempts = [...Array(3)];

    for await (const [index] of attempts.entries()) {
      try {
        if (index) await new Promise((resolve) => setTimeout(resolve, 1000));
        return await createCheckoutSession(params).unwrap();
      } catch (error) {
        if (index === attempts.length - 1) throw error;
      }
    }
  };

  const handleSuccess = (response: HelcimTransactionResponse) => {
    cleanupHelcimElements();
    onPaymentComplete(response);
    onClose();
  };

  const handlePayment = async () => {
    try {
      setError(null);
      setPaymentState('processing');

      const numericAmount =
        typeof paymentAmount === 'string'
          ? parseFloat(paymentAmount)
          : paymentAmount;

      if (isNaN(numericAmount) || numericAmount <= 0) {
        setError('Please enter a valid amount.');
        setPaymentState('error');
        return;
      }

      if (numericAmount > remainingBalance) {
        setError('Payment amount cannot exceed the total invoice amount.');
        setPaymentState('error');
        return;
      }

      const checkoutResponse = await createCheckoutWithRetry(
        createCheckoutSession,
        {
          amount: numericAmount,
          currency: currency,
        },
      );

      if (typeof window.appendHelcimPayIframe !== 'function') {
        throw new Error(
          'Payment system not initialized. Please refresh and try again.',
        );
      }

      const helcimResponsePromise = new Promise<HelcimPayResponse>(
        (resolve, reject) => {
          const timeoutId = setTimeout(() => {
            const helcimIframe = document.querySelector(
              'iframe[src*="helcim"]',
            );
            if (
              !helcimIframe ||
              (helcimIframe as HTMLElement).style.display === 'none'
            ) {
              cleanup();
              return;
            }

            cleanup();
            setShowTimeoutModal(true);
            setTimeout(() => {
              setShowTimeoutModal(false);
              navigate(0);
            }, 3000);
          }, 300000);

          const cleanup = () => {
            window.removeEventListener('message', messageHandler);
            clearTimeout(timeoutId);
          };

          const messageHandler = (event: MessageEvent) => {
            // Only process messages from Helcim
            if (event.origin === 'https://secure.helcim.app') {
              try {
                const messageData = event.data;

                // Checker for Helcim iFrame if closed
                const helcimIframe = document.querySelector(
                  'iframe[src*="helcim"]',
                );
                if (
                  !helcimIframe ||
                  (helcimIframe as HTMLElement).style.display === 'none'
                ) {
                  cleanup();
                  setPaymentState('idle');
                  reject(new Error(t('Payment window closed')));
                  return;
                }

                if (messageData.eventName) {
                  switch (messageData.eventName) {
                    case 'helcim-pay-close':
                    case 'helcim-pay-cancel':
                    case 'helcim-pay-exit':
                      cleanup();
                      setPaymentState('idle');
                      reject(new Error('Payment cancelled'));
                      return;

                    case 'helcim-pay-initialized':
                      return;
                  }
                }

                if (
                  messageData.eventStatus === 'SUCCESS' &&
                  messageData.eventMessage
                ) {
                  try {
                    const parsedMessage = JSON.parse(messageData.eventMessage);

                    if (parsedMessage.data) {
                      const responseData =
                        typeof parsedMessage.data === 'string'
                          ? JSON.parse(parsedMessage.data)
                          : parsedMessage.data;

                      if (responseData.hash && responseData.data) {
                        cleanup();
                        resolve({
                          hash: responseData.hash,
                          data: responseData.data,
                        });
                        return;
                      }
                    }
                  } catch (parseError) {
                    cleanup();
                    setPaymentState('error');
                    reject(parseError);
                  }
                }

                if (
                  messageData.eventStatus === 'CANCELLED' ||
                  messageData.status === 'CANCELLED' ||
                  (messageData.eventMessage &&
                    typeof messageData.eventMessage === 'string' &&
                    messageData.eventMessage.toLowerCase().includes('cancel'))
                ) {
                  cleanup();
                  setPaymentState('idle');
                  reject(new Error('Payment cancelled'));
                  return;
                }
              } catch (error) {
                cleanup();
                setShowTimeoutModal(false);
                setPaymentState('error');
                reject(error);
              }
            }
          };

          window.addEventListener('message', messageHandler);
        },
      );

      try {
        window.appendHelcimPayIframe(checkoutResponse.checkoutToken, true);
      } catch (error) {
        throw new Error('Failed to initialize payment form. Please try again.');
      }

      try {
        const helcimResponse = await helcimResponsePromise;

        setPaymentState('confirming');

        if (!isHelcimPayResponse(helcimResponse)) {
          throw new Error('Invalid payment response format');
        }

        const confirmedPayment = await confirmPayment({
          invoiceId,
          responseData: JSON.stringify(helcimResponse.data),
          responseHash: helcimResponse.hash,
          secretToken: checkoutResponse.secretToken,
        }).unwrap();

        setPaymentState('success');
        handleSuccess(confirmedPayment);

        await new Promise((resolve) => setTimeout(resolve, 1500));
        onClose();
      } catch (error) {
        if (error instanceof z.ZodError) {
          setError('Invalid payment response received');
        } else if (error instanceof Error) {
          setError(error.message);
        } else {
          setError('Payment confirmation failed');
        }
        setPaymentState('error');
      }
    } catch (error) {
      cleanupHelcimElements();
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('An unexpected error occurred');
      }
      setPaymentState('error');
    }
  };

  const cleanupHelcimElements = useCallback(() => {
    const helcimIframes = document.querySelectorAll('iframe[src*="helcim"]');
    helcimIframes.forEach((iframe) => iframe.remove());

    const recaptchaElements = document.querySelectorAll('.grecaptcha-badge');
    recaptchaElements.forEach((element) => element.remove());

    const overlays = document.querySelectorAll('[class*="helcim-overlay"]');
    overlays.forEach((overlay) => overlay.remove());
  }, []);

  const resetModalState = useCallback(() => {
    setPaymentAmount(remainingBalance.toString());
    setError(null);
    setIsProcessingPayment(false);
  }, [remainingBalance]);

  // Helcim Modal Behavior
  useEffect(() => {
    return () => {
      setPaymentState('idle');
      setError(null);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setPaymentState('idle');
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    setPaymentAmount(remainingBalance);
  }, [remainingBalance]);

  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setIsProcessingPayment(false);
      setShowTimeoutModal(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      resetModalState();
    }
  }, [isOpen, resetModalState]);

  // Cleanup Helcim iFrame Elements
  useEffect(() => {
    if (!isOpen) {
      cleanupHelcimElements();
    }
    return () => {
      cleanupHelcimElements();
    };
  }, [cleanupHelcimElements, isOpen]);

  useEffect(() => {
    if (paymentState === 'processing') {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.removedNodes.forEach((node) => {
            if (
              node instanceof HTMLIFrameElement &&
              node.tagName === 'IFRAME' &&
              node.src?.includes('helcim')
            ) {
              setPaymentState('idle');
            }
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      return () => observer.disconnect();
    }
  }, [paymentState]);

  // Session Timeout Modal
  const TimeoutRedirectModal = ({ show }: { show: boolean }) => {
    return show
      ? createPortal(
          <div
            className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50"
            style={{ animation: 'none' }}
          >
            <div
              className="rounded-lg bg-white p-6 shadow-xl"
              style={{ minWidth: '400px' }}
            >
              <div className="mb-4 text-lg font-semibold">
                {t('helcim_session_timeout_title')}
              </div>{' '}
              <div className="flex flex-col items-center space-y-4">
                <AlertCircle className="h-12 w-12 text-red-500" />
                <div className="text-center">
                  {t('helcim_session_timeout_description')
                    .split('\n')
                    .map((line: string, i: number) => (
                      <p key={i}>{line}</p>
                    ))}
                </div>
                <Spinner />
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;
  };

  // Proper Prompt Handling for Helcim Payment
  const PaymentStatus = ({
    state,
    error,
  }: {
    state: PaymentState;
    error: string | null;
  }) => {
    const getContent = () => {
      switch (state) {
        case 'idle':
          return null;
        case 'processing':
          return {
            icon: <Spinner className="mb-4" $size={30} $borderWidth={2} />,
            message: t('Processing Payment'),
            description: t('processing_payment_description'),
          };
        case 'confirming':
          return {
            icon: <Spinner className="mb-4" $size={30} $borderWidth={2} />,
            message: t('Confirming Payment'),
            description: t('confirming_payment_description'),
          };
        case 'success':
          return {
            icon: <CheckCircle className="mb-4 h-12 w-12 text-green-500" />,
            message: t('Payment Successful'),
            description: t('payment_successful_description'),
          };
        case 'error':
          return {
            icon: <AlertCircle className="mb-4 h-12 w-12 text-red-500" />,
            message: t('Payment Failed'),
            description: error || t('payment_error_description'),
          };
      }
    };

    const content = getContent();
    if (!content) return null;

    return (
      <div
        className="absolute inset-0 z-50 flex items-center justify-center bg-white/90"
        style={{
          outline: '2px solid #7b5ce5',
          borderRadius: '8px',
        }}
      >
        <div className="relative flex flex-col items-center rounded-lg border border-gray-200 bg-white p-6 text-center shadow-lg">
          {content.icon}
          <h3 className="mb-2 text-lg font-semibold text-gray-900">
            {content.message}
          </h3>
          <p className="text-sm text-gray-600">{content.description}</p>
          {state === 'error' && (
            <Button
              size="compact"
              onClick={() => {
                setPaymentState('idle');
                setError(null);
                cleanupHelcimElements();
                resetModalState();
              }}
              className="mt-4"
            >
              {t('Try Again')}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <TimeoutRedirectModal show={showTimeoutModal} />
      <Modal
        onClose={() => {
          if (paymentState !== 'processing' && paymentState !== 'confirming') {
            onClose();
          }
        }}
        isOpen={isOpen}
        animate
        closeable={
          paymentState !== 'processing' && paymentState !== 'confirming'
        }
      >
        <ModalHeader>{t('Payment Details')}</ModalHeader>
        <ModalBody className="grid gap-4">
          <PaymentStatus state={paymentState} error={error} />
          <Card>
            <StyledBody>
              <div className="flex items-start justify-between">
                <div
                  className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-md"
                  style={{ backgroundColor: '#7b5ce5' }}
                >
                  <CreditCard className="h-10 w-10 text-white" />
                </div>
                <Tag
                  size="small"
                  variant="light"
                  kind="positive"
                  closeable={false}
                >
                  {currency}
                </Tag>
              </div>
              <div>
                <div className="mt-4 font-prompt">{t('Helcim Payment')}</div>
                <p className="text-sm text-gray-500">
                  {t('Secure payment processing via Helcim')}
                </p>
              </div>
            </StyledBody>
          </Card>

          {error && (
            <div className="flex items-center gap-2 text-red-500">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="flex flex-col space-y-2">
            <div>
              <div className="font-prompt font-semibold">{t('Amount')}</div>
              <p className="text-sm text-gray-500">
                {t('Enter the payment amount')}
              </p>
              <p className="mb-2 text-sm font-semibold text-gray-700">
                {t('original_total_amount')}: {currency === 'USD' ? '$' : 'C$'}
                {remainingBalance.toFixed(2)}
              </p>
            </div>
            <Input
              type="text"
              value={formatAmount(paymentAmount)}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                  setPaymentAmount(value);
                }
              }}
              placeholder="0.00"
              disabled={isProcessing || remainingBalance <= 0}
              startEnhancer={<span>{currency === 'USD' ? '$' : 'C$'}</span>}
              overrides={{
                Input: {
                  style: {
                    textAlign: 'left',
                    '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button':
                      {
                        '-webkit-appearance': 'none',
                        margin: 0,
                      },
                    '-moz-appearance': 'textfield',
                  },
                },
                StartEnhancer: {
                  style: {
                    backgroundColor: 'transparent',
                    paddingRight: '4px',
                  },
                },
              }}
            />
            {Number(paymentAmount) > remainingBalance && (
              <div className="flex items-center gap-2 text-red-500">
                <AlertCircle className="h-8 w-8" />
                <span className="text-sm">
                  {t('payment_amount_exceeds_total')}
                </span>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <ModalButton
            kind="tertiary"
            onClick={onClose}
            disabled={isProcessing || isProcessingPayment}
          >
            {t('Cancel')}
          </ModalButton>
          <ModalButton
            onClick={handlePayment}
            isLoading={isProcessing}
            disabled={
              isProcessing ||
              isProcessingPayment ||
              Number(paymentAmount) <= 0 ||
              Number(paymentAmount) > remainingBalance ||
              remainingBalance <= 0
            }
          >
            {t('Process Payment')}
          </ModalButton>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default PaymentModal;
